/**
 * anlage-ballsets — Block 3 der Vorschau: die Set-Varianten-Matrix.
 *
 * Eine Zeile je geplanter Set-Variante, also Original-Variante x Setgroesse. Die Zeile mit
 * `istHauptvariantenQuelle` ist hervorgehoben und beschriftet: von ihr erbt die
 * Hauptvariante des Ballsets ihre Preise und ihren EK — das ist die einzige Zeile, deren
 * Zahlen zweimal in Plenty landen, und der Nutzer soll sie ohne Nachrechnen finden.
 *
 * Fehlende Preise stehen als Strich da, nie als 0,00 € (siehe `eurOderStrich`).
 */
import type { SetVarianteAnzeige } from '../typen';
import { eurOderStrich, PREIS_SPALTEN, zahl } from '../formate';

interface Props {
  setVarianten: SetVarianteAnzeige[];
}

/** Attribute als „Name: Wert" — inklusive „Set Bälle Anzahl: 10 Bälle". */
function attributeText(attribute: Array<{ attributName: string; wertName: string }>): string {
  const teile = attribute
    .map((a) => `${a.attributName}: ${a.wertName}`.trim())
    .filter((t) => t !== ':');
  return teile.length > 0 ? teile.join(' · ') : '—';
}

export default function SetMatrixTabelle({ setVarianten }: Props) {
  return (
    <div className="border border-[#DAD9D9] rounded-lg bg-white p-4">
      <h2 className="text-sm font-bold text-[#1A1A1A]">
        Set-Varianten <span className="text-gray-500">({zahl(setVarianten.length)})</span>
      </h2>

      <div className="mt-2 overflow-x-auto">
        <table className="w-full text-xs">
          <thead className="bg-[#1A1A1A] text-white">
            <tr>
              <th className="px-2 py-1.5 text-left font-medium">Original-Variante</th>
              <th className="px-2 py-1.5 text-right font-medium">Setgröße</th>
              <th className="px-2 py-1.5 text-left font-medium">Attribute</th>
              {PREIS_SPALTEN.map((s) => (
                <th key={s.id} className="px-2 py-1.5 text-right font-medium">
                  {s.titel}
                </th>
              ))}
              <th className="px-2 py-1.5 text-right font-medium">EK</th>
              <th className="px-2 py-1.5 text-left font-medium">Paket</th>
            </tr>
          </thead>
          <tbody>
            {setVarianten.map((v) => (
              <tr
                key={`${v.originalVariationId}-${v.setgroesse}`}
                className={`border-b border-[#DAD9D9] last:border-0 ${
                  v.istHauptvariantenQuelle ? 'font-semibold bg-[#E9FF57]/20' : ''
                }`}
              >
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {v.originalNumber}
                  <span className="text-gray-400"> #{v.originalVariationId}</span>
                  {v.istHauptvariantenQuelle && (
                    <span className="ml-1.5 rounded bg-[#E9FF57] px-1.5 py-0.5 text-[10px] font-semibold text-[#1A1A1A]">
                      Quelle der Hauptvariante
                    </span>
                  )}
                </td>
                <td className="px-2 py-1.5 text-right">{zahl(v.setgroesse)}</td>
                <td className="px-2 py-1.5">{attributeText(v.attribute)}</td>
                {PREIS_SPALTEN.map((s) => (
                  <td key={s.id} className="px-2 py-1.5 text-right whitespace-nowrap">
                    {eurOderStrich(v.preise[s.id])}
                  </td>
                ))}
                <td className="px-2 py-1.5 text-right whitespace-nowrap">
                  {eurOderStrich(v.purchasePrice)}
                </td>
                <td className="px-2 py-1.5 whitespace-nowrap">
                  {zahl(v.paket.componentQuantity)} × Variante {v.paket.componentVariationId}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <p className="mt-2 text-[11px] text-gray-500">
        Die hervorgehobene Zeile ist die Quelle der Ballset-Hauptvariante: sie erbt deren
        Preise und EK, bekommt aber selbst weder das Set-Attribut noch ein Paket.
      </p>
    </div>
  );
}
